/**
 * Aipron CoPilot™ - API Service
 * =============================
 * Frontend service for CoPilot chat interactions with AODB tools.
 */

import api from './api';

// --- Types ---

export interface ActionProposal {
    type: string;
    action_id: string;
    action_type: string;
    title: string;
    description: string;
    params: Record<string, string>;
    requires_confirmation: boolean;
}

export interface NavigationLink {
    type: string;
    name: string;
    path: string;
    icon: string;
    category?: string;
}

export interface ChatMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
    sources?: string[];
    actions?: ActionProposal[];
    navigation?: NavigationLink[];
}

export interface ChatRequest {
    message: string;
    project_id: string;
    session_id?: string;
    use_agent?: boolean;
}

export interface ChatResponse {
    response: string;
    sources: string[];
    session_id: string;
    actions: ActionProposal[];
    navigation: NavigationLink[];
}

export interface ActionResponse {
    success: boolean;
    message: string;
    action?: Record<string, unknown>;
}

export interface CopilotStatus {
    status: string;
    mode: 'demo' | 'production';
    rag_enabled: boolean;
    agent_enabled: boolean;
}

// --- API Functions ---

/**
 * Send a message to the CoPilot and get a response.
 */
export async function sendMessage(
    message: string,
    projectId: string,
    sessionId?: string,
    useAgent: boolean = true
): Promise<ChatResponse> {
    const response = await api.post<ChatResponse>('/copilot/chat', {
        message,
        project_id: projectId,
        session_id: sessionId,
        use_agent: useAgent,
    });
    return response.data;
}

/**
 * Confirm a pending action.
 */
export async function confirmAction(actionId: string): Promise<ActionResponse> {
    const response = await api.post<ActionResponse>('/copilot/action/confirm', {
        action_id: actionId,
    });
    return response.data;
}

/**
 * Cancel a pending action.
 */
export async function cancelAction(actionId: string): Promise<ActionResponse> {
    const response = await api.post<ActionResponse>('/copilot/action/cancel', {
        action_id: actionId,
    });
    return response.data;
}

/**
 * Get details of a pending action.
 */
export async function getAction(actionId: string): Promise<Record<string, unknown>> {
    const response = await api.get<Record<string, unknown>>(`/copilot/action/${actionId}`);
    return response.data;
}

/**
 * Clear a chat session's history.
 */
export async function clearSession(sessionId: string): Promise<boolean> {
    const response = await api.delete<{ success: boolean }>('/copilot/session', {
        data: { session_id: sessionId },
    });
    return response.data.success;
}

/**
 * Get CoPilot status and configuration.
 */
export async function getCopilotStatus(): Promise<CopilotStatus> {
    const response = await api.get<CopilotStatus>('/copilot/status');
    return response.data;
}

// --- Utility Functions ---

/**
 * Generate a unique message ID.
 */
export function generateMessageId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Generate a unique session ID.
 */
export function generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}
